import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { useCreateNominatedPerson } from '@/hooks/use-comply';
import { handleApiError } from '@/lib/api-client';
import type { CreateNominatedPersonPayload, NominatedRole } from '@/lib/types';

export function PersonnelNewPage() {
  const navigate = useNavigate();
  const createMutation = useCreateNominatedPerson();

  const [form, setForm] = useState<CreateNominatedPersonPayload>({
    role: 'ramr',
    role_title: '',
    name: '',
    email: '',
    phone: '',
    appointment_date: '',
    required_cert_level: 'none',
    notes: '',
  });

  const [error, setError] = useState<string | null>(null);

  const updateField = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!form.name || !form.appointment_date) {
      setError('Name and appointment date are required.');
      return;
    }

    try {
      await createMutation.mutateAsync(form);
      navigate('/compliance/personnel');
    } catch (err) {
      const apiError = handleApiError(err);
      setError(apiError.message);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center gap-4">
        <Button asChild variant="ghost" size="icon">
          <Link to="/compliance/personnel">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Appoint Role</h1>
          <p className="text-gray-600 mt-1">
            Nominate a person for a QP-300 compliance role
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Role Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="role">Role Type *</Label>
                <Select
                  value={form.role}
                  onValueChange={(v) => updateField('role', v as NominatedRole)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ramr">
                      RAMR <span className="ml-1 text-red-500">*</span>
                    </SelectItem>
                    <SelectItem value="technical_authority">
                      Technical Authority <span className="ml-1 text-red-500">*</span>
                    </SelectItem>
                    <SelectItem value="primary_contact">
                      Primary contact <span className="ml-1 text-red-500">*</span>
                    </SelectItem>
                    <SelectItem value="equipment_selection">
                      Selection of equipment <span className="ml-1 text-red-500">*</span>
                    </SelectItem>
                    <SelectItem value="detailed_inspection">
                      Detailed inspection of equipment <span className="ml-1 text-red-500">*</span>
                    </SelectItem>
                    <SelectItem value="quarterly_safety_returns">
                      Quarterly safety returns <span className="ml-1 text-red-500">*</span>
                    </SelectItem>
                    <SelectItem value="safety_officer">Safety Officer</SelectItem>
                    <SelectItem value="quality_manager">Quality Manager</SelectItem>
                    <SelectItem value="training_manager">Training Manager</SelectItem>
                    <SelectItem value="communications">Communications / IRATA safety returns appointed person</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="role_title">Custom Title (optional)</Label>
                <Input
                  id="role_title"
                  value={form.role_title}
                  onChange={(e) => updateField('role_title', e.target.value)}
                  placeholder="e.g. Senior Safety Advisor"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Person Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => updateField('email', e.target.value)}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={form.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="appointment_date">Appointment Date *</Label>
                <Input
                  id="appointment_date"
                  type="date"
                  value={form.appointment_date}
                  onChange={(e) => updateField('appointment_date', e.target.value)}
                  required
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Certification Requirement</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="required_cert_level">Required Certification Level</Label>
              <Select
                value={form.required_cert_level || 'none'}
                onValueChange={(v) => updateField('required_cert_level', v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="irata_1">IRATA Level 1</SelectItem>
                  <SelectItem value="irata_2">IRATA Level 2</SelectItem>
                  <SelectItem value="irata_3">IRATA Level 3</SelectItem>
                  <SelectItem value="sprat_1">SPRAT Level 1</SelectItem>
                  <SelectItem value="sprat_2">SPRAT Level 2</SelectItem>
                  <SelectItem value="sprat_3">SPRAT Level 3</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                If set, the system will validate this person holds a current cert at this
                level via their tech profile.
              </p>
            </div>
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={form.notes}
                onChange={(e) => updateField('notes', e.target.value)}
                rows={3}
                placeholder="Additional context or instructions..."
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-3">
          <Button type="submit" disabled={createMutation.isPending}>
            {createMutation.isPending && (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            )}
            Appoint Role
          </Button>
          <Button type="button" variant="outline" asChild>
            <Link to="/compliance/personnel">Cancel</Link>
          </Button>
        </div>
      </form>
    </div>
  );
}
